import React from "react";
import { PreJobCheckListV2 } from "./types";
import { PreJobCheckListV2Form } from "./PreJobCheckListV2Form";
import { useInspectionDispatchV2 } from "../../contexts/inspection-context-v2";
import { useMutation, useQuery } from "@apollo/client";
import {
  Mutation,
  MutationUpdateInvoiceArgs,
  MutationUpdateJobArgs,
  MutationUpsertVehicleArgs,
  Query,
  QueryGetJobArgs,
  QueryGetVehicleArgs,
  UpdateJobInput,
  VehicleInput,
  VehicleType,
} from "../../generated/nest-graphql";
import { UPDATE_JOB } from "../../graphql/mutations/updateJob";
import { UPDATE_INVOICE } from "../../graphql/mutations/updateInvoice";
import { flow } from "lodash";
import { preJobCheckListV2UpdateJobSpec } from "../Jobs/jobDetailsSpec";
import { keys } from "ramda";
import { showSuccessAlert, showWarningAlert } from "../../actions";
import { useDispatch } from "../../contexts/snackbar-context";
import { UPSERT_VEHICLE } from "../../graphql/mutations/upsertVehicle";
import { GET_JOB } from "../../graphql/queries/getJob";
import { GET_VEHICLE } from "../../graphql/queries/getVehicles";
import { clearDraft, readDraft } from "../../lib/inspectionDraftStore";
import {
  countFilledFields,
  inspectionClosedByTech,
  inspectionDraftRestored,
  inspectionOpened,
  inspectionSubmitted,
} from "../../lib/inspectionTelemetry";

export type PreJobCheckListV2FormValues = PreJobCheckListV2 & {
  odometer: string;
};

export const mapVehicleTypeToVehicleInput = (vehicle: VehicleType): VehicleInput => ({
  contactId: vehicle.contactId,
  make: vehicle.make,
  model: vehicle.model,
  year: vehicle.year,
  name: vehicle.name,
  subModel: vehicle.subModel,
  id: vehicle.id,
  licensePlate: vehicle.licensePlate,
  licensePlateState: vehicle.licensePlateState,
  hasDrumBrakes: vehicle.vehicleTechnicalInfo.hasDrumBrakes,
  notes: vehicle.notes,
  vin: vehicle.vin,
  inactive: vehicle.inactive,
  odometer: vehicle.odometer,
  wheelTorque: vehicle.vehicleTechnicalInfo.wheelTorque,
  parkingBrake: vehicle.vehicleTechnicalInfo.parkingBrake,
  frontRotorsDiscardThicknessInches: vehicle.vehicleTechnicalInfo.frontRotorsDiscardThicknessInches,
  rearRotorsDiscardThicknessInches: vehicle.vehicleTechnicalInfo.rearRotorsDiscardThicknessInches,
  // upsert rebuilds vehicleTechnicalInfo wholesale -- carry this through so
  // submitting the checklist does not erase the Ops-entered capacity.
  oilCapacityQuarts: vehicle.vehicleTechnicalInfo.oilCapacityQuarts,
});

export const PreJobCheckListV2Dialog = ({
  jobId,
  invoiceId,
  open,
  onClose,
  initialValues,
  isReadOnly = false,
}: {
  jobId: string;
  invoiceId?: string;
  open: boolean;
  onClose: () => void;
  initialValues: PreJobCheckListV2FormValues;
  isReadOnly?: boolean;
}) => {
  const dispatch = useDispatch();
  const inspectionDispatch = useInspectionDispatchV2();
  const [updateJob] = useMutation<Mutation, MutationUpdateJobArgs>(UPDATE_JOB);
  const [updateInvoice] = useMutation<Mutation, MutationUpdateInvoiceArgs>(UPDATE_INVOICE);
  const [upsertVehicle] = useMutation<Mutation, MutationUpsertVehicleArgs>(UPSERT_VEHICLE);

  const { data, loading } = useQuery<Query, QueryGetJobArgs>(GET_JOB, {
    variables: {
      id: jobId,
    },
    fetchPolicy: "cache-first",
    returnPartialData: true,
  });

  const vehicleId = data?.getJob?.vehicleId;

  const { data: vehicleData, loading: loadingVehicleData } = useQuery<Query, QueryGetVehicleArgs>(GET_VEHICLE, {
    variables: {
      id: vehicleId,
    },
    skip: !vehicleId,
    fetchPolicy: "cache-first",
    returnPartialData: true,
  });

  const vehicle = vehicleData?.getVehicle;

  const initValues: PreJobCheckListV2FormValues = {
    ...initialValues,
    licensePlate: vehicle?.licensePlate || initialValues.licensePlate,
    licensePlateState: vehicle?.licensePlateState || initialValues.licensePlateState,
  };

  /*
   * One "open session" per time the technician opens the inspection.
   *
   * The values the form starts from are resolved once, when the dialog opens,
   * and then held still. They used to be recomputed on every render and fed to
   * Formik with `enableReinitialize`, so any change in the underlying job or
   * vehicle data — a poll, a refetch, a cache write from an unrelated query —
   * reset the form out from under the technician while they were filling it in.
   *
   * Bumping `id` remounts Formik, which is what makes a reopen pick up a
   * restored draft. The dialog stays mounted when closed so the post-submit
   * "send the report?" prompt still has somewhere to live.
   */
  const [openSession, setOpenSession] = React.useState<{
    id: number;
    initialValues: PreJobCheckListV2FormValues;
  } | null>(null);
  const snapshotTakenRef = React.useRef(false);

  // The job and vehicle both feed the values the form starts from, so there is
  // nothing worth snapshotting until they have arrived.
  const isReady = !((loading && !data) || (loadingVehicleData && !vehicleData));

  React.useEffect(() => {
    if (!open) {
      snapshotTakenRef.current = false;
      return;
    }
    if (snapshotTakenRef.current || !isReady) return;
    snapshotTakenRef.current = true;

    inspectionOpened(jobId);

    // Read-only means we're viewing a closed inspection; never let a leftover
    // draft shadow what was actually submitted.
    const draft = isReadOnly ? null : readDraft<PreJobCheckListV2FormValues>(jobId);

    if (draft) {
      inspectionDraftRestored(jobId, countFilledFields(draft.values), Date.now() - draft.savedAt);
      showSuccessAlert(dispatch, "Restored your unsaved inspection");
      setOpenSession((prev) => ({ id: (prev?.id ?? 0) + 1, initialValues: draft.values }));
      return;
    }

    setOpenSession((prev) => ({ id: (prev?.id ?? 0) + 1, initialValues: initValues }));
    // `initValues` is rebuilt on every render by design; snapshotting it here,
    // once per open, is the whole point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isReady, jobId, isReadOnly]);

  const updateOnClose = (values: PreJobCheckListV2FormValues) => {
    // The draft is intentionally kept: the technician may be stepping away and
    // coming back. It is only cleared once the inspection is submitted.
    inspectionClosedByTech(jobId, values);
    inspectionDispatch({ type: "UPDATE_FULL_INSPECTION", payload: values });
    onClose();
  };
  const onSubmit = async (values: PreJobCheckListV2FormValues) => {
    inspectionDispatch({ type: "UPDATE_FULL_INSPECTION", payload: values });
    const input: UpdateJobInput = flow(preJobCheckListV2UpdateJobSpec)(values as PreJobCheckListV2FormValues);
    // A failed vehicle update no longer blocks the inspection: it still submits, with a warning instead of "Success".
    const vehicleSaved = await upsertVehicle({
      variables: {
        vehicleInputData: mapVehicleTypeToVehicleInput({
          ...vehicle,
          odometer: values.odometer,
          licensePlate: values.licensePlate,
          licensePlateState: values.licensePlateState,
        }),
      },
    }).then(
      () => true,
      () => false
    );

    await updateJob({
      variables: {
        id: jobId,
        updateJobInput: input,
      },
    });
    if (invoiceId && keys(values).length && input) {
      updateInvoice({
        variables: {
          id: invoiceId,
          updateInvoiceInput: input,
        },
      });
    }
    // Only now is the report safely on the server, so this is the one place the
    // local draft may be dropped.
    clearDraft(jobId, "submitted");
    inspectionSubmitted(jobId, values);

    onClose();
    if (vehicleSaved) showSuccessAlert(dispatch, "Success");
    else showWarningAlert(dispatch, "Inspection saved, but the vehicle's mileage and plate could not be updated");
  };

  /*
   * Only bail out while we genuinely have nothing to show. This used to be
   * `loading || loadingVehicleData`, which also fired whenever an already-loaded
   * query went back into flight — unmounting the open form and discarding the
   * technician's work to render a frame of nothing.
   */
  if (!isReady) {
    return null;
  }

  return (
    <PreJobCheckListV2Form
      key={`pre-job-2-${openSession?.id ?? 0}`}
      initialValues={openSession?.initialValues ?? initValues}
      onSubmit={onSubmit}
      open={open}
      onClose={updateOnClose}
      jobId={jobId}
      isReadOnly={isReadOnly}
    />
  );
};
